import React, { useCallback, useEffect, useMemo, useState } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { Icon, Popup, List } from "semantic-ui-react";
import { WebStorage, WebStorageNames } from "../../../utils";
import {
  APP_LANGUAGE_CODES,
  applyAppLanguage,
  DEFAULT_PRIMARY_LANGUAGE,
  LANGUAGE_NATIVE_NAMES,
  getPrimaryLanguageFromUserInfo,
  getStoredUserLanguage,
  normalizeAppLanguageKey,
} from "../../../utils/appLanguages";
import {
  fetchAccountUserDetailsAndSyncLanguage,
  updatePrimaryLanguage,
} from "../../../api/userLanguageApi";
import LanguageFlag from "./LanguageFlag";
import "./LanguageSelector.css";

/**
 * `guest` is for logged-out pages (login, register, forgot password): the choice is kept
 * in the browser only (no account calls, no userInfo record), and each language is shown
 * by its own name so it can be found whatever language the page is currently in.
 */
const LanguageSelector = ({ skipUserDetailsSync = false, guest = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(DEFAULT_PRIMARY_LANGUAGE);

  useEffect(() => {
    const userInfo = WebStorage.getLocalStore(WebStorageNames.UserInfo);
    const fromUser = getPrimaryLanguageFromUserInfo(userInfo);
    const fromStorage = getStoredUserLanguage();
    const initial = normalizeAppLanguageKey(
      fromUser || fromStorage || DEFAULT_PRIMARY_LANGUAGE
    );
    setCurrent(initial);
    applyAppLanguage(initial, { persistUserInfo: !guest });

    // Auth responses already include PrimaryLanguage; skip GetUserDetails on
    // onboarding, on logged-out pages, and whenever language is already persisted locally.
    if (skipUserDetailsSync || guest || fromUser || fromStorage) {
      return;
    }

    fetchAccountUserDetailsAndSyncLanguage()
      .then((lang) => {
        if (lang) {
          setCurrent(lang);
          applyAppLanguage(lang);
        }
      })
      .catch(() => {});
  }, [skipUserDetailsSync, guest]);

  useEffect(() => {
    const onLanguageChanged = (lng) => {
      setCurrent(normalizeAppLanguageKey(lng));
    };
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  const options = useMemo(
    () =>
      APP_LANGUAGE_CODES.map((code) => ({
        code,
        label: guest ? LANGUAGE_NATIVE_NAMES[code] || code : t(`languages.${code}`) || code,
      })),
    [t, guest]
  );

  const handleSelect = useCallback(
    async (code) => {
      if (code === current || saving) return;
      const previous = current;
      setCurrent(code);
      setOpen(false);
      setSaving(true);
      try {
        await applyAppLanguage(code, { persistUserInfo: !guest });
        if (!guest) await updatePrimaryLanguage(code);
      } catch {
        setCurrent(previous);
        await applyAppLanguage(previous, { persistUserInfo: !guest });
      } finally {
        setSaving(false);
      }
    },
    [current, saving, guest]
  );

  const currentOption = options.find((o) => o.code === current) || options[0];

  const trigger = (
    <div
      className="language-selector-trigger"
      role="button"
      tabIndex={0}
      aria-label={t("app.header.language")}
      aria-haspopup="listbox"
      aria-expanded={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen((v) => !v);
        }
      }}
    >
      <LanguageFlag code={currentOption?.code} className="language-flag-trigger" />
      <span className="language-code">
        {(currentOption?.code || DEFAULT_PRIMARY_LANGUAGE).replace("_", "-").toUpperCase()}
      </span>
      <Icon name="chevron down" className="dropdown-chevron" size="small" />
    </div>
  );

  const content = (
    <div className="language-selector-popup">
      <div className="popup-header">
        <h4>{t("app.header.language")}</h4>
        <p>{t("app.header.languageHint")}</p>
      </div>
      <List selection className="language-selector-list">
        {options.map((opt) => (
          <List.Item
            key={opt.code}
            className={`language-option${opt.code === current ? " is-active" : ""}`}
            onClick={() => handleSelect(opt.code)}
            role="option"
            aria-selected={opt.code === current}
          >
            <LanguageFlag code={opt.code} className="option-flag" />
            <span className="option-label">{opt.label}</span>
            {opt.code === current && <Icon name="check" className="option-check" />}
          </List.Item>
        ))}
      </List>
      {saving && (
        <div className="language-selector-saving">
          <Icon name="circle notch" loading size="small" />
          {t("app.header.languageSaving")}
        </div>
      )}
    </div>
  );

  return (
    <Popup
      trigger={trigger}
      content={content}
      on="click"
      position="bottom right"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      className="language-selector"
      size="small"
      hideOnScroll
    />
  );
};

export default LanguageSelector;
